/**
 * /dashboard/search — global cross-entity search results.
 *
 * Fan-out searches against startups, opportunities, agreements, founders
 * and entities. Each section calls its own existing list endpoint with
 * a `q` parameter (most of them already support it; we fall back to a
 * client-side filter when they don't).
 *
 * Renders a grouped list with deep-link CTAs. Empty state mirrors the
 * dark Palantir aesthetic of the rest of the dashboard.
 */
/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Rocket, Briefcase, FileSignature, Users, Building2, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

const SOURCES = [
  { key: 'startups',      label: 'Startups',      icon: Rocket,        path: '/startups',          to: (r) => `/dashboard/startups/${r.startup_id || r.id}`,        title: (r) => r.name || r.company || 'Untitled', sub: (r) => r.sector || r.industry || '' },
  { key: 'opportunities', label: 'Opportunities', icon: Briefcase,     path: '/opportunities',     to: (r) => `/dashboard/opportunities/${r.opportunity_id || r.id}`,title: (r) => r.title || r.name || 'Untitled', sub: (r) => r.stage || r.status || '' },
  { key: 'agreements',    label: 'Agreements',    icon: FileSignature, path: '/agreements',        to: (r) => `/dashboard/agreements/${r.agreement_id || r.id}`,    title: (r) => r.title || r.counterparty || 'Untitled', sub: (r) => r.type || r.status || '' },
  { key: 'founders',      label: 'Founders',      icon: Users,         path: '/founders',          to: (r) => `/dashboard/founders/${r.founder_id || r.user_id || r.id}`, title: (r) => r.name || r.email || 'Untitled', sub: (r) => r.company || r.email || '' },
  { key: 'entities',      label: 'Entities',      icon: Building2,     path: '/entities-v2',       to: (r) => `/dashboard/entities/${r.entity_id || r.id}`,         title: (r) => r.legal_name || r.brand_name || r.name || 'Untitled', sub: (r) => r.cr_number || r.jurisdiction || '' },
];

function tokenMatch(haystack, q) {
  if (!q) return true;
  if (!haystack) return false;
  return String(haystack).toLowerCase().includes(q.toLowerCase());
}

export default function GlobalSearch() {
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const [results, setResults] = useState({});      // { sourceKey: rows[] }
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q) { setResults({}); return; }
    let cancel = false;
    setLoading(true);
    (async () => {
      const out = {};
      await Promise.all(SOURCES.map(async (s) => {
        try {
          const { data } = await api.get(s.path, { params: { q, search: q, limit: 5 } });
          const rows = Array.isArray(data) ? data : (data?.items || data?.results || []);
          // Some endpoints ignore ?q — apply a client-side fallback filter.
          const filtered = rows.filter((r) =>
            tokenMatch(s.title(r), q) || tokenMatch(s.sub(r), q),
          ).slice(0, 5);
          if (!cancel) out[s.key] = filtered;
        } catch {
          if (!cancel) out[s.key] = [];
        }
      }));
      if (!cancel) {
        setResults(out);
        setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [q]);

  const totalHits = useMemo(
    () => Object.values(results).reduce((acc, rows) => acc + (rows?.length || 0), 0),
    [results],
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] opacity-60 font-bold">
          <Search size={12}/> Global search
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold mt-2">
          {q ? <>Results for <span className="text-[var(--brand-primary,#8B3FC5)]">&ldquo;{q}&rdquo;</span></>
             : 'Search the workspace'}
        </h1>
        {q && !loading && (
          <p className="text-sm opacity-60 mt-1" data-testid="search-result-count">
            {totalHits === 0 ? 'No matches yet.' : `${totalHits} match${totalHits === 1 ? '' : 'es'} across ${SOURCES.length} sources.`}
          </p>
        )}
        {!q && (
          <p className="text-sm opacity-60 mt-1">
            Use the search bar at the top of the page. We look across startups, opportunities, agreements, founders and entities.
          </p>
        )}
      </div>

      {loading && (
        <div className="text-sm opacity-60 pal-mono uppercase tracking-[0.18em]" data-testid="search-loading">
          Searching…
        </div>
      )}

      {q && !loading && SOURCES.map((s) => {
        const rows = results[s.key] || [];
        if (rows.length === 0) return null;
        const Icon = s.icon;
        return (
          <section key={s.key} className="ve-card p-5" data-testid={`search-section-${s.key}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon size={14} className="text-[var(--brand-primary,#8B3FC5)]"/>
              <h2 className="text-sm font-bold uppercase tracking-[0.18em]">{s.label}</h2>
              <span className="text-[10px] opacity-50 pal-mono ml-1">{rows.length}</span>
            </div>
            <ul className="divide-y divide-slate-200/40 dark:divide-white/5">
              {rows.map((r, i) => (
                <li key={`${s.key}-${i}`} className="py-2.5">
                  <Link
                    to={s.to(r)}
                    className="flex items-start gap-3 hover:opacity-90 group"
                    data-testid={`search-hit-${s.key}-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.title(r)}</div>
                      {s.sub(r) && (
                        <div className="text-[11px] opacity-60 mt-0.5 truncate">{s.sub(r)}</div>
                      )}
                    </div>
                    <ArrowRight size={14} className="opacity-40 group-hover:opacity-100 transition-opacity mt-1 shrink-0"/>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {q && !loading && totalHits === 0 && (
        <div className="ve-card p-8 text-center" data-testid="search-empty">
          <Search size={28} className="mx-auto opacity-40"/>
          <div className="mt-3 text-sm font-semibold">No matches for &ldquo;{q}&rdquo;</div>
          <p className="text-xs opacity-60 mt-1">Try a shorter query or check spelling.</p>
        </div>
      )}
    </div>
  );
}
